import { useEffect, useState } from 'react'
import { Clock, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { fetchBranch, updateBranch } from '@/lib/api/branches'
import { BUSINESS_DAYS, normalizeBusinessHours } from '@/lib/businessHours'
import { cn } from '@/lib/utils'

import { SettingsSection } from './SettingsSection'

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function HoursSettingsSection({ embedded = false }) {
  const { activeBranchId } = useBranch()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [hours, setHours] = useState(() => normalizeBusinessHours())

  useEffect(() => {
    if (!activeBranchId) {
      return undefined
    }

    let active = true
    setLoading(true)

    fetchBranch(activeBranchId)
      .then((branch) => {
        if (active) {
          setHours(normalizeBusinessHours(branch.business_hours))
        }
      })
      .catch((err) => {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load hours.')
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [activeBranchId])

  /**
   * @param {string} dayKey
   * @param {'open' | 'close'} field
   * @param {string} value
   */
  function updateHour(dayKey, field, value) {
    setHours((current) => ({
      ...current,
      [dayKey]: {
        ...current[dayKey],
        [field]: value,
      },
    }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!activeBranchId) {
      return
    }

    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await updateBranch(activeBranchId, { business_hours: hours })
      setSuccess('Opening hours saved.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save hours.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSection
      id="hours"
      icon={Clock}
      title="Opening hours"
      description="Open and close times for each day"
      embedded={embedded}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            {BUSINESS_DAYS.map((day) => (
              <div
                key={day.key}
                className="grid grid-cols-[minmax(0,1fr)_7rem_7rem] items-center gap-2 sm:grid-cols-[8rem_1fr_1fr]"
              >
                <span className="text-sm font-medium text-foreground">{day.label}</span>
                <input
                  type="time"
                  value={hours[day.key]?.open ?? ''}
                  onChange={(e) => updateHour(day.key, 'open', e.target.value)}
                  className={inputClass}
                  aria-label={`${day.label} open`}
                />
                <input
                  type="time"
                  value={hours[day.key]?.close ?? ''}
                  onChange={(e) => updateHour(day.key, 'close', e.target.value)}
                  className={inputClass}
                  aria-label={`${day.label} close`}
                />
              </div>
            ))}
          </div>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save hours'}
          </Button>
        </form>
      )}
    </SettingsSection>
  )
}
